"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlatformError } from "./client";
import { platformFetch, platformSend } from "./browser";

export type Profile = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  birthDate: string | null;
  email: string | null;
  countryId: string | null;
  createdAt: string;
  firstDepositAt: string | null;
  isHidden: boolean;
};

export type Country = { id: string; cca2: string; name: string };

const nullOnUnauthorized = async <T>(read: () => Promise<T>) => {
  try {
    return await read();
  } catch (error) {
    if (error instanceof PlatformError && error.kind === "unauthorized") {
      return null;
    }

    throw error;
  }
};

export const useProfile = (enabled: boolean) =>
  useQuery({
    queryKey: ["profile"],
    enabled,
    staleTime: 60_000,
    queryFn: () =>
      nullOnUnauthorized(async () => (await platformFetch<{ data: Profile }>("profile")).data),
  });

export const useCountries = (enabled = true) =>
  useQuery({
    queryKey: ["countries"],
    enabled,
    staleTime: 24 * 60 * 60_000,
    queryFn: async () => (await platformFetch<{ data: Country[] }>("countries")).data,
  });

export const useCountryName = (countryId: string | null | undefined) => {
  const countries = useCountries(Boolean(countryId));

  return countries.data?.find((country) => country.id === countryId)?.name ?? null;
};

export const useReferral = (enabled: boolean) =>
  useQuery({
    queryKey: ["referral"],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: () =>
      nullOnUnauthorized(
        async () =>
          (await platformFetch<{ data: { id: string; code: string } | null }>("referral")).data,
      ),
  });

export const useHiddenMode = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (enabled: boolean) =>
      (
        await platformSend<{ data: { id: string; isHidden: boolean } }>(
          "profile/hidden-mode",
          { enabled },
          "PATCH",
        )
      ).data,
    onSettled: () => {
      client.invalidateQueries({ queryKey: ["profile"] });
      client.invalidateQueries({ queryKey: ["whoami"] });
    },
  });
};

export type ProfileDetails = {
  firstName: string;
  lastName: string;
  birthDate: string | null;
  countryId: string;
};

export const useSetProfile = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (details: ProfileDetails) =>
      (await platformSend<{ data: Profile }>("profile", details, "PATCH")).data,
    onSettled: () => {
      client.invalidateQueries({ queryKey: ["profile"] });
      client.invalidateQueries({ queryKey: ["whoami"] });
    },
  });
};

export type LinkedWallet = {
  id: string;
  address: string;
  environment: "evm" | "solana";
  createdAt: string;
};

export type Referral = { id: string; code: string; createdAt: string };

export const useWallets = (enabled: boolean) =>
  useQuery({
    queryKey: ["wallets"],
    enabled,
    staleTime: 60_000,
    queryFn: () =>
      nullOnUnauthorized(
        async () => (await platformFetch<{ data: LinkedWallet[] }>("wallets")).data,
      ),
  });

export const useReferrals = (enabled: boolean) =>
  useQuery({
    queryKey: ["referrals"],
    enabled,
    staleTime: 60_000,
    queryFn: () =>
      nullOnUnauthorized(
        async () => (await platformFetch<{ data: Referral[] }>("referrals")).data,
      ),
  });

export const useCreateReferral = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) =>
      (await platformSend<{ data: Referral }>("referrals", { code })).data,
    onSettled: () => {
      client.invalidateQueries({ queryKey: ["referrals"] });
    },
  });
};

export const useRevokeReferral = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (referralId: string) =>
      (
        await platformSend<{ data: Referral & { deletedAt: string | null } }>(
          `referrals/${referralId}`,
          {},
          "DELETE",
        )
      ).data,
    onSettled: () => {
      client.invalidateQueries({ queryKey: ["referrals"] });
    },
  });
};

export const useNicknameAvailable = (nickname: string, enabled: boolean) =>
  useQuery({
    queryKey: ["profile", "nickname", "check", nickname],
    enabled: enabled && nickname.trim().length > 0,
    staleTime: 30_000,
    retry: false,
    queryFn: () =>
      nullOnUnauthorized(
        async () =>
          (
            await platformFetch<{ data: { isAvailable?: boolean; available?: boolean } }>(
              "profile/nickname/check",
              { nickname: nickname.trim() },
            )
          ).data,
      ),
  });

export const useRandomNickname = () =>
  useMutation({
    mutationFn: async () =>
      (await platformFetch<{ data: { nickname: string } }>("profile/nickname/random")).data.nickname,
  });

export const useSetNickname = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (nickname: string) =>
      (
        await platformSend<{ data: { nickname: string } }>(
          "profile/nickname",
          { nickname },
          "PATCH",
        )
      ).data,
    onSettled: () => {
      client.invalidateQueries({ queryKey: ["whoami"] });
      client.invalidateQueries({ queryKey: ["profile"] });
    },
  });
};

export const useSendEmailVerification = () =>
  useMutation({
    mutationFn: (email: string) =>
      platformSend<{ data: unknown }>("profile/email/verification", { email }),
  });
